import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Alert,
  Chip,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Divider,
  CircularProgress,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Description,
  CheckCircleOutline,
  CloudDone,
  RateReview,
  Videocam,
  Groups,
  Refresh,
  HistoryToggleOff,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useOrgId } from "@/shared/auth/orgContext";
import { KPICard } from "@/pages/Analytics/components/KPICard";
import { currentProfileQuery } from "@/features/users/api/profileQueries";
import { useStationActivity } from "@/features/stationAdmin/hooks/useStationActivity";
import {
  aggregateMetricsForDay,
  listActivityDays,
  widenToLocalDayStart,
} from "@/features/stationAdmin/api/stationActivityApi";
import { AiSummaryCard } from "./components/AiSummaryCard";
import { ActivityFeed } from "./components/ActivityFeed";
import { formatActivityDay } from "./components/activityFeedView";

function formatWindowStart(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "recently";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const StationAdminDashboard: React.FC = () => {
  const theme = useTheme();
  const orgId = useOrgId();
  const {
    window,
    metrics,
    feed,
    officerActivity,
    reports,
    evidence,
    isLoading,
    isFetching,
    isError,
    evidenceError,
    refetch,
    rosterSettled,
    rosterSize,
    rosterError,
  } = useStationActivity();

  // Grouping for the Overview KPIs + Station Pulse: the precise
  // since-last-login window (default; "Last 48 hours" when no prior login) or
  // one specific local day with activity. Day buckets start at local midnight
  // (the fetch widens the first day accordingly) and cap at the frozen window
  // end. The since-login lens, AI briefing, and activity feed stay scoped to
  // the exact window (the feed has its own Group-by control).
  const [selectedDayKey, setSelectedDayKey] = useState<string>("all");
  const dayOptions = useMemo(
    () =>
      listActivityDays(reports, evidence, {
        start: widenToLocalDayStart(window.windowStart),
        end: window.windowEnd,
      }),
    [reports, evidence, window.windowStart, window.windowEnd],
  );
  // A stale key (e.g. after a refetch changes the available current-metadata
  // rows) falls back to "all" rather than rendering zeros for a vanished day.
  const selectedDay =
    dayOptions.find((d) => d.key === selectedDayKey) ?? null;
  const dayMetrics = useMemo(
    () =>
      selectedDay
        ? aggregateMetricsForDay(
            reports,
            evidence,
            selectedDay.start,
            metrics.truncated,
            window.windowEnd,
          )
        : metrics,
    [selectedDay, reports, evidence, metrics, window.windowEnd],
  );
  // MainComponent keeps this cached for the session (staleTime Infinity), so
  // this is a cache read, not a new fetch. `|| undefined` (not ??): a missing
  // membership maps to "", which must fall through to the backend's generic
  // "your station" rather than an empty STATION line in the prompt.
  const { data: profile } = useQuery(currentProfileQuery());
  const stationName = profile?.stationName || undefined;

  const windowLabel = window.hasPriorLogin
    ? `Since your last login · ${formatWindowStart(window.windowStart)}`
    : "Last 48 hours · no prior login on record";
  // Prose form of the same window, shared with the feed subheader so every
  // surface names the period identically.
  const windowDescription = window.hasPriorLogin
    ? `since your last login (${formatWindowStart(window.windowStart)})`
    : "in the last 48 hours";

  // Clamped: in-window attribution ids can exceed the CURRENT roster (e.g.
  // officers deactivated since their activity), and >100% renders an
  // out-of-range LinearProgress.
  const activeRatio =
    rosterSize > 0
      ? Math.min(
          100,
          Math.round((dayMetrics.activeOfficers / rosterSize) * 100),
        )
      : 0;

  return (
    <Container maxWidth="xl" sx={{ overflow: "auto" }}>
      <Box sx={{ p: 3, pb: 4, minHeight: "100vh" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Station Overview
            </Typography>
            <Chip
              icon={<HistoryToggleOff sx={{ fontSize: 16 }} />}
              label={windowLabel}
              size="small"
              sx={{
                mt: 0.75,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: "text.secondary",
                fontWeight: 500,
              }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isFetching ? (
                // 18px matches the icon it replaces (small-Button startIcon
                // font-size), so the header doesn't jitter during a refetch.
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Refresh />
              )
            }
            onClick={refetch}
            disabled={isLoading || isFetching}
          >
            Refresh
          </Button>
        </Box>

        {isError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={refetch}
                disabled={isFetching}
              >
                Retry
              </Button>
            }
          >
            Failed to load station activity. Please try again.
          </Alert>
        ) : (
          <>
            {metrics.truncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This station had a large volume of activity in this period — the
                figures below are a lower bound.
              </Alert>
            )}
            {evidenceError && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={refetch}
                    disabled={isFetching}
                  >
                    Retry
                  </Button>
                }
              >
                Evidence activity couldn&apos;t be loaded. Evidence upload
                counts are hidden, and any officer active only through an
                evidence upload is excluded — so Active Officers is a lower
                bound. Report counts are unaffected.
              </Alert>
            )}

            {/* Day grouping for the KPIs + Station Pulse. A plain flex row
                (not Stack): role="toolbar" + gap works with wrapping, which
                Stack's margin-based spacing does not. Chips wrap at sm+; on
                phones the row stays a horizontal scroller. */}
            {!isLoading && dayOptions.length > 0 && (
              <Box
                role="toolbar"
                aria-label="Group metrics by day"
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                  pb: 0.5,
                  flexWrap: { xs: "nowrap", sm: "wrap" },
                  overflowX: { xs: "auto", sm: "visible" },
                }}
              >
                <Chip
                  label={
                    window.hasPriorLogin ? "Since last login" : "Last 48 hours"
                  }
                  size="small"
                  color={selectedDay ? "default" : "primary"}
                  variant={selectedDay ? "outlined" : "filled"}
                  aria-pressed={!selectedDay}
                  onClick={() => setSelectedDayKey("all")}
                />
                {dayOptions.map((day) => (
                  <Chip
                    key={day.key}
                    label={formatActivityDay(day.start)}
                    size="small"
                    color={selectedDay?.key === day.key ? "primary" : "default"}
                    variant={selectedDay?.key === day.key ? "filled" : "outlined"}
                    aria-pressed={selectedDay?.key === day.key}
                    onClick={() => setSelectedDayKey(day.key)}
                  />
                ))}
              </Box>
            )}
            {selectedDay && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: -1, mb: 2 }}
              >
                Calendar-day view — may include activity from before{" "}
                {window.hasPriorLogin
                  ? "your last login"
                  : "the 48-hour window"}
                , but excludes activity after this dashboard snapshot. The
                activity feed and briefing below stay scoped to{" "}
                {window.hasPriorLogin ? "your last login" : "the last 48 hours"}
                .
              </Typography>
            )}

            {/* KPI grid */}
            <Grid container spacing={2} sx={{ mb: 3, alignItems: "stretch" }}>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Skeleton variant="rounded" height={116} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))
                : (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Reports Created"
                        description="New reports started by station officers in the selected period."
                        value={dayMetrics.reportsCreated}
                        icon={<Description />}
                        color="primary"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Reports Signed"
                        description="Reports signed or finalized (signed, CO-approved, or RMS-committed) with activity in the selected period."
                        value={dayMetrics.reportsSigned}
                        icon={<CheckCircleOutline />}
                        color="success"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Committed to RMS"
                        description="Reports pushed to the records management system in the selected period."
                        value={dayMetrics.reportsCommitted}
                        icon={<CloudDone />}
                        color="info"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Awaiting Review"
                        description="Reports currently pending review, in CO review, or with changes requested."
                        value={dayMetrics.reportsInReview}
                        icon={<RateReview />}
                        color="warning"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Evidence Uploaded"
                        description="Body-worn-camera and other evidence files ingested in the selected period."
                        // A failed evidence fetch must read as unavailable, not
                        // as a real zero (the warning banner above explains).
                        value={evidenceError ? "—" : dayMetrics.evidenceUploaded}
                        icon={<Videocam />}
                        color="info"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <KPICard
                        title="Active Officers"
                        description="Distinct officers who created/edited a report or uploaded evidence."
                        value={dayMetrics.activeOfficers}
                        unit={rosterSize > 0 ? `/ ${rosterSize}` : undefined}
                        icon={<Groups />}
                        color="primary"
                        progress={
                          rosterSize > 0
                            ? { value: activeRatio, target: `${activeRatio}% of roster active` }
                            : undefined
                        }
                      />
                    </Grid>
                  </>
                )}
            </Grid>

            {/* AI activity briefing */}
            <Box sx={{ mb: 3 }}>
              <AiSummaryCard
                orgId={orgId ?? ""}
                window={window}
                metrics={metrics}
                feed={feed}
                officerActivity={officerActivity}
                stationName={stationName}
                // Wait for the roster too: evidence events summarized before
                // names resolve would bake "Unknown officer" into a briefing
                // cached for the whole session.
                activityReady={!isLoading && rosterSettled}
                // Suppress the briefing when evidence failed: the metrics/feed
                // silently omit all evidence, so a generated summary would
                // assert or imply zero evidence activity.
                evidenceUnavailable={evidenceError}
              />
            </Box>

            {/* Activity feed + station pulse */}
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <ActivityFeed
                  events={feed}
                  loading={isLoading}
                  windowDescription={windowDescription}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Station Pulse
                    </Typography>
                    <Stack spacing={2} divider={<Divider flexItem />}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {selectedDay
                            ? `Officers active — ${formatActivityDay(selectedDay.start)}`
                            : "Officers active this period"}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {dayMetrics.activeOfficers}
                          {rosterSize > 0 && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {" "}/ {rosterSize} on roster
                            </Typography>
                          )}
                        </Typography>
                        {rosterSize > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={activeRatio}
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                        )}
                        {evidenceError && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Lower bound — officers active only through evidence
                            uploads are excluded while evidence activity is
                            unavailable.
                          </Typography>
                        )}
                        {rosterError && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Station roster unavailable — officer names may show
                            as IDs. Use Refresh to retry.
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Reports touched
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {dayMetrics.reportsTouched}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Needs your attention
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color={dayMetrics.reportsInReview > 0 ? "warning.main" : "text.primary"}
                        >
                          {dayMetrics.reportsInReview} awaiting review
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Container>
  );
};

export default StationAdminDashboard;
